import React, { useMemo, useEffect, useCallback, useRef } from 'react'
import { subscribe } from 'react-contextual'
import { get, isEqual } from 'lodash-es'
import { FocusMode, PortfolioPage, SidebarNav } from '../../../../views/components'
import PortfoliosProvider from '../../PortfoliosProvider'
import { useSelector } from 'react-redux'
import { portfolioApiSelectors } from '../../../api/portfolio'
import { portfolioSelectors } from '../../state'
import { getCurrentLeaseType } from 'modules/ui/ChatBot'
import { pathOr, split, replace, isEmpty, trim } from 'ramda'
import PortfolioStatusLabel from '../PortfolioStatusLabel'

const PortfolioWizard = ({
  match,
  goBack,
  currentPortfolioId,
  redirect,
  resetLayout,
  setCurrentPortfolioId,
  resetCurrentPortfolio,
  pathname,
  PortfolioWizardSidebarItems,
  propertyDescription,
  streetAddress,
  buildingAddress,
}: any) => {
  const url = get(match, 'url')
  const portfolioId = get(match, 'params.id')

  const leaseType = useSelector(getCurrentLeaseType)

  const getTitle = () => {
    const prefix = pathOr('', [0], split('Contract', leaseType))

    const propertyAddress = propertyDescription
      ? `${propertyDescription}, ${buildingAddress || streetAddress}`
      : buildingAddress || streetAddress
    const title = portfolioId === 'new' ? 'New Lease' : !propertyAddress ? 'Edit Lease' : propertyAddress

    const isTitleEmpty = isEmpty(trim(replace(',', '', title)))

    return `${!isTitleEmpty && prefix ? prefix + ': ' : ''}${!isTitleEmpty ? title : ''}`
  }

  const isNewPartyModalOpen = useSelector(portfolioSelectors.isNewPartyModalOpen)

  useEffect(() => {
    if (portfolioId === 'new') {
      resetCurrentPortfolio()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId])

  const ContentComponent = useMemo(
    () =>
      function PortfolioContent() {
        return (
          <PortfolioPage
            url={url}
            portfolioId={portfolioId}
            currentPortfolioId={currentPortfolioId}
            redirectTo={redirect}
            setCurrentPortfolioId={setCurrentPortfolioId}
          />
          // eslint-disable-next-line react-hooks/exhaustive-deps
        )
      },
    [url, portfolioId, currentPortfolioId],
  )

  const isDraftPortfolio = useSelector(state => portfolioApiSelectors.isDraftPortfolio(state)(portfolioId))

  // `getPortfolioNavbarItems` is an unmemoized selector, so it returns a new array on every
  // Redux dispatch even when the items are unchanged. Hold a content-stable reference so the
  // sidebar (and the FocusMode memo below) only recompute when the items actually change.
  const sidebarItemsRef = useRef(PortfolioWizardSidebarItems)
  if (!isEqual(sidebarItemsRef.current, PortfolioWizardSidebarItems)) {
    sidebarItemsRef.current = PortfolioWizardSidebarItems
  }
  const stableSidebarItems = sidebarItemsRef.current

  // eslint-disable-next-line react/display-name
  const SidebarComponent = useMemo(
    () =>
      function PortfolioSidebar() {
        return (
          <SidebarNav
            url={pathname}
            items={stableSidebarItems}
            onClick={redirect}
            isDraftPortfolio={isDraftPortfolio}
          />
        )
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, stableSidebarItems, isDraftPortfolio],
  )

  // Compute the title as a plain string value so the memo below keys on the value
  // (stable across unrelated re-renders) rather than a function identity.
  const title = getTitle()

  // `redirect`, `goBack` and `resetLayout` are re-created on every provider render but are
  // behaviourally stable (they just dispatch/navigate). Hold them in refs so `handleClose` —
  // and thus the FocusMode memo — stays stable.
  const redirectRef = useRef(redirect)
  redirectRef.current = redirect
  const goBackRef = useRef(goBack)
  goBackRef.current = goBack
  const resetLayoutRef = useRef(resetLayout)
  resetLayoutRef.current = resetLayout
  const handleClose = useCallback(() => {
    // A new lease has no route to return to (`/leases/new` just renders the list). Reset the
    // layout so any open primary panel closes, then return to the previous route the user came
    // from. Editing an existing lease returns to that lease's view as before.
    if (portfolioId === 'new') {
      resetLayoutRef.current()
      goBackRef.current()
    } else {
      redirectRef.current(`/leases/${portfolioId}`)
    }
  }, [portfolioId])

  // Return a stable element reference whenever nothing meaningful changed. React then
  // skips re-rendering the entire FocusMode subtree (header, sidebar, content) during
  // unrelated Redux/provider churn, instead of re-rendering it on every dispatch.
  return useMemo(
    () => (
      <FocusMode
        isOpen
        titlePrefix={<PortfolioStatusLabel portfolioId={portfolioId} />}
        title={title}
        ContentComponent={ContentComponent}
        SidebarComponent={SidebarComponent}
        onClose={handleClose}
        disableScroll={isNewPartyModalOpen}
      />
    ),
    [title, portfolioId, ContentComponent, SidebarComponent, handleClose, isNewPartyModalOpen],
  )
}

export default subscribe(
  PortfoliosProvider,
  ({
    goBack,
    currentPortfolioId,
    redirect,
    resetLayout,
    setCurrentPortfolioId,
    resetCurrentPortfolio,
    pathname,
    PortfolioWizardSidebarItems,
    propertyDescription,
    streetAddress,
    buildingAddress,
  }: any) => ({
    goBack,
    currentPortfolioId,
    redirect,
    resetLayout,
    setCurrentPortfolioId,
    resetCurrentPortfolio,
    pathname,
    PortfolioWizardSidebarItems,
    propertyDescription,
    streetAddress,
    buildingAddress,
  }),
)(PortfolioWizard)
